import { createFocusTrap } from "focus-trap";
import { listen, qs, qsa } from "@fluorescent/dom";
import updateInnerHTML from "@/lib/update-inner-html";
import { disableBodyScroll, enableBodyScroll } from "body-scroll-lock";
import getMediaQuery from "@/lib/media-queries";

const selectors = {
  drawer: "[data-filter-drawer]",
  drawerWash: "[data-filter-drawer-wash]",
  drawerForm: "[data-filter-form]",
  drawerToggle: "[data-drawer-toggle]",
  drawerTitle: "[data-filter-drawer-title]",
  drawerApply: "[data-filter-drawer-apply]",
  closeDrawer: "[data-close-drawer]",
};

const FilterDrawer = node => {
  if (!node) return false;

  const drawerEl = qs(selectors.drawer, node);

  if (!drawerEl) return false;

  const drawerToggleEl = qs(selectors.drawerToggle, node);
  let drawerCloseEls, drawerFormEl, drawerWash;
  let focusTrap = createFocusTrap(drawerEl, {
    allowOutsideClick: true,
  });
  let events = [];

  init();

  function init() {
    drawerFormEl = qs(selectors.drawerForm, drawerEl);
    drawerCloseEls = qsa(
      `${selectors.closeDrawer}, ${selectors.drawerApply}`,
      drawerEl
    );

    drawerWash = qs(selectors.drawerWash, node);

    events.push(
      listen(drawerWash, "click", hideDrawer),
      listen(drawerCloseEls, "click", hideDrawer),
      listen(drawerFormEl, "keydown", ({ keyCode }) => {
        if (keyCode === 27) hideDrawer();
      })
    );
  }

  function toggleDrawer() {
    const ariaHidden = drawerEl.getAttribute("aria-hidden") === "true";

    if (ariaHidden) {
      showDrawer();
    } else {
      hideDrawer();
    }
  }

  function showDrawer() {
    if (drawerToggleEl) {
      drawerToggleEl.setAttribute("aria-expanded", true);
    }
    drawerEl.setAttribute("aria-hidden", false);
    document.body.setAttribute("data-drawer-modal-open", "true");

    if (window.matchMedia(getMediaQuery("below-1024")).matches) {
      focusTrap.activate();
      disableBodyScroll(node, {
        allowTouchMove: el => {
          while (el && el !== document.body) {
            if (el.getAttribute("data-scroll-lock-ignore") !== null) {
              return true;
            }
            el = el.parentNode;
          }
        },
        reserveScrollBarGap: true,
      });
    }
  }

  function hideDrawer() {
    if (drawerToggleEl) {
      drawerToggleEl.setAttribute("aria-expanded", false);
    }
    drawerEl.setAttribute("aria-hidden", true);

    if (window.matchMedia(getMediaQuery("below-1024")).matches) {
      focusTrap?.deactivate();
      enableBodyScroll(node);
    }

    // Delay by 500ms to allow the drawer to close before any CSS styles are applied.
    setTimeout(() => {
      document.body.setAttribute("data-drawer-modal-open", "false");
    }, 500);
  }

  function renderFilters(doc) {
    updateInnerHTML(`${selectors.drawer} ${selectors.drawerTitle}`, doc);
    updateInnerHTML(`${selectors.drawer} ${selectors.drawerApply}`, doc);

    const docDrawerEl = qs(selectors.drawer, doc);

    drawerEl.dataset.filtersActive = docDrawerEl.dataset.filtersActive;
    unload();
    init();
  }

  function unload() {
    events.forEach(unsubscribe => unsubscribe());
    events = [];
  }

  return {
    toggleDrawer,
    renderFilters,
    unload,
  };
};

export default FilterDrawer;
